import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { 
  Tag, 
  Trophy, 
  ShirtIcon, 
  Users, 
  Newspaper, 
  Star, 
  AlertTriangle, 
  MessageCircle, 
  HelpCircle, 
  WifiOff, 
  Flame, 
  Info, 
  RefreshCcw,
  Clock,
  User
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const CATEGORY_CONFIG = {
  "Discount Code": { icon: Tag, color: "bg-gradient-to-r from-green-500 to-emerald-600" },
  "Point Opportunities": { icon: Trophy, color: "bg-gradient-to-r from-yellow-500 to-orange-600" },
  "Merchandise": { icon: ShirtIcon, color: "bg-gradient-to-r from-purple-500 to-violet-600" },
  "Lockeroom Updates": { icon: Users, color: "bg-gradient-to-r from-blue-500 to-indigo-600" },
  "News": { icon: Newspaper, color: "bg-gradient-to-r from-cyan-500 to-teal-600" },
  "SpotLight": { icon: Star, color: "bg-gradient-to-r from-yellow-400 to-amber-500" },
  "Alert": { icon: AlertTriangle, color: "bg-gradient-to-r from-red-500 to-rose-600" },
  "FeedBack": { icon: MessageCircle, color: "bg-gradient-to-r from-pink-500 to-rose-500" },
  "FAQ": { icon: HelpCircle, color: "bg-gradient-to-r from-indigo-500 to-purple-600" },
  "Outage": { icon: WifiOff, color: "bg-gradient-to-r from-gray-600 to-slate-700" },
  "Fire": { icon: Flame, color: "bg-gradient-to-r from-red-600 to-orange-700" },
  "Need to Know": { icon: Info, color: "bg-gradient-to-r from-blue-600 to-cyan-600" },
  "Player Loop": { icon: RefreshCcw, color: "bg-gradient-to-r from-emerald-500 to-green-600" }
};

interface AdminFeedPost {
  id: string;
  category: string;
  title: string;
  description?: string;
  how_to_get_info?: string;
  priority: number;
  created_at: string;
  admin_id: string;
}

interface AdminFeedPostsProps {
  refreshTrigger?: number;
}

export const AdminFeedPosts = ({ refreshTrigger }: AdminFeedPostsProps) => {
  const [posts, setPosts] = useState<AdminFeedPost[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAdminPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_feed_posts')
        .select('*')
        .eq('is_active', true)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching admin posts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminPosts();
  }, [refreshTrigger]);

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="h-16 bg-muted" />
            <CardContent className="h-24 bg-muted/50" />
          </Card>
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {posts.map((post) => {
        const categoryConfig = CATEGORY_CONFIG[post.category as keyof typeof CATEGORY_CONFIG];
        const IconComponent = categoryConfig?.icon || Info;
        const priorityLabel = post.priority === 3 ? "URGENT" : post.priority === 2 ? "HIGH" : "NORMAL";
        
        return (
          <Card 
            key={post.id} 
            className={`relative border-2 ${
              post.priority === 3 
                ? "border-red-500 shadow-red-500/20" 
                : post.priority === 2 
                ? "border-orange-500 shadow-orange-500/20" 
                : "border-primary shadow-primary/20"
            } shadow-lg transition-all hover:shadow-xl`}
          >
            {/* Priority Indicator */}
            {post.priority > 1 && (
              <div className={`absolute -top-2 -right-2 px-2 py-1 rounded-full text-xs font-bold text-white ${
                post.priority === 3 ? "bg-red-600" : "bg-orange-600"
              }`}>
                {priorityLabel}
              </div>
            )}

            <CardHeader className={`${categoryConfig?.color || "bg-gradient-to-r from-primary to-primary-foreground"} text-white rounded-t-lg`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <IconComponent className="h-6 w-6" />
                  <div>
                    <Badge variant="secondary" className="bg-white/20 text-white border-white/30 mb-1">
                      {post.category}
                    </Badge>
                    <h3 className="text-lg font-semibold">{post.title}</h3>
                  </div>
                </div>
                <User className="h-5 w-5 opacity-80" />
              </div>
            </CardHeader>

            <CardContent className="pt-4 space-y-3">
              {post.description && (
                <p className="text-foreground/80">{post.description}</p>
              )}
              
              {post.how_to_get_info && (
                <div className="bg-muted/50 p-3 rounded-lg">
                  <h4 className="font-medium text-sm mb-1 text-primary">How to get it:</h4>
                  <p className="text-sm text-muted-foreground">{post.how_to_get_info}</p>
                </div>
              )}

              <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t">
                <Clock className="h-3 w-3" />
                <span>Posted {formatDistanceToNow(new Date(post.created_at))} ago</span>
                <span className="ml-auto bg-primary/10 text-primary px-2 py-1 rounded-full font-medium">
                  ADMIN ANNOUNCEMENT
                </span>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};